import React from 'react'
import { TextField, SelectField } from '../../Base/Form'
import { z } from 'zod'
import SanitizeHtml from '../../Base/SanitizeHtml/SanitizeHtml'
import { useSelector } from 'react-redux'
import { useWatch } from 'react-hook-form'

export const paymentSchema = (dictionary) =>
  z.object({
    cardNumber: z
      .string({
        required_error: dictionary.cardNumberRequiredText,
      })
      .min(1, dictionary.cardNumberRequiredText)
      .min(13, dictionary.cardNumberLengthText)
      .max(19, dictionary.cardNumberLengthText)
      .regex(/^\d{13,19}$/, dictionary.cardNumberLengthText),

    cardHolder: z
      .string({
        required_error: dictionary.cardHolderRequiredText,
      })
      .min(1, dictionary.cardHolderRequiredText)
      .min(2, dictionary.cardHolderLengthText)
      .max(45, dictionary.cardHolderLengthText)
      .regex(/^[A-Za-z\d /:_().,+-]*$/, dictionary.cardHolderLengthText),

    expiryMonth: z
      .string({
        required_error: dictionary.expiryMonthRequiredText,
      })
      .min(1, dictionary.expiryMonthRequiredText),

    expiryYear: z
      .string({
        required_error: dictionary.expiryYearRequiredText,
      })
      .min(1, dictionary.expiryYearRequiredText),

    cvv: z
      .string({
        required_error: dictionary.cvvRequiredText,
      })
      .min(1, dictionary.cvvRequiredText)
      .regex(/^\d{3,4}$/, dictionary.cvvLengthText),
  })

const detectCardType = (cardNumber) => {
  // Remove spaces and non-digits
  const cleanNumber = cardNumber.replace(/\D/g, '')

  if (!cleanNumber) return null

  // Visa: starts with 4
  if (/^4/.test(cleanNumber)) {
    return 'visa'
  }

  // Mastercard: starts with 5[1-5] or 2[2-7]
  if (/^5[1-5]/.test(cleanNumber) || /^2[2-7]/.test(cleanNumber)) {
    return 'mastercard'
  }

  // American Express: starts with 34 or 37
  if (/^3[47]/.test(cleanNumber)) {
    return 'americanexpress'
  }

  // Diners: starts with 30, 36, 38, or 300-305
  if (/^3[068]/.test(cleanNumber) || /^30[0-5]/.test(cleanNumber)) {
    return 'diners'
  }

  // JCB: starts with 35
  if (/^35/.test(cleanNumber)) {
    return 'jcb'
  }

  // Discover: starts with 6011, 622126-622925, 644-649, or 65
  if (
    /^6011/.test(cleanNumber) ||
    /^622[1-9]/.test(cleanNumber) ||
    /^64[4-9]/.test(cleanNumber) ||
    /^65/.test(cleanNumber)
  ) {
    return 'discover'
  }

  return null
}

/**
 * Component for the payment form
 *
 * @param {Object} control - The control passed from the parent component
 * @param {Object} errors - The errors passed from the parent component
 *
 * @returns {ReactElement} The payment form component
 */
const Payment = ({ control, errors }) => {
  const { dictionary, expiryMonthsOptions, expiryYearsOptions, paymentImages } =
    window.bookingData || window.modifyData

  const isBooking = !!window?.bookingData

  const cardNumber = useWatch({
    control,
    name: 'cardNumber',
    defaultValue: '',
  })

  const detectedCardType = detectCardType(cardNumber)

  // const validatePayment = (data) => {
  //   try {
  //     paymentSchema.parse(data)
  //     return { valid: true, errors: {} }
  //   } catch (e) {
  //     return { valid: false, errors: e.errors }
  //   }
  // }

  const { paymentNotice, selectedVehicle } = useSelector(
    (state) => state.booking
  )
  const { isGREngine } = selectedVehicle || {}
  const showNotice = paymentNotice && isBooking

  return (
    <div className="s-booking-form__form-group">
      <div className="row">
        <div className="col-lg-6 mb-24">
          <span className="s-booking-form__form-group__title">
            {dictionary.paymentLabel}
          </span>
        </div>
      </div>

      <div className="s-booking-form__wrapper">
        <div
          className={`row s-booking-form__payment__header ${
            showNotice ? 'mb-4' : 'mb-20'
          }`}>
          <span className="col-11 title">{dictionary.paymentMethodLabel}</span>
          <div className="col-1 icon">
            <i className="icon-credit-card"></i>
          </div>
        </div>

        {showNotice && (
          <div className="mb-24">
            <SanitizeHtml html={paymentNotice} />
          </div>
        )}

        <div className="mb-24">
          <TextField
            name="cardNumber"
            label={dictionary.paymentCardNumberLabel}
            control={control}
            error={errors.cardNumber}
          />
        </div>

        <div className="mb-24">
          <TextField
            name="cardHolder"
            label={dictionary.paymentCardNameLabel}
            control={control}
            error={errors.cardHolder}
          />
        </div>

        <div className="row">
          <div className="col-lg-4 col-6 mb-24">
            <SelectField
              name="expiryMonth"
              label={dictionary.paymentCardExpiryMonthLabel}
              options={expiryMonthsOptions}
              control={control}
              error={errors.expiryMonth}
            />
          </div>
          <div className="col-lg-4 col-6 mb-24">
            <SelectField
              name="expiryYear"
              label={dictionary.paymentCardExpiryYearLabel}
              options={expiryYearsOptions}
              control={control}
              error={errors.expiryYear}
            />
          </div>
          <div className="col-lg-4 mb-24">
            <TextField
              name="cvv"
              label={dictionary.paymentCardCVVLabel}
              type="password"
              control={control}
              error={errors.cvv}
              maxLength={4}
              info={dictionary.paymentCVVTooltip}
            />
          </div>
        </div>

        <div className="s-booking-form__payment__images">
          <ul>
            {paymentImages
              ?.filter((image) => image.src)
              ?.filter((image) => {
                if (isGREngine) {
                  return ['visa', 'mastercard', 'americanexpress'].includes(
                    image.name
                  )
                }
                return true
              })
              ?.map((image) => (
                <li key={image.name}>
                  <img
                    src={image.src}
                    alt={image.alt}
                    className={
                      image.name !== detectedCardType && cardNumber?.length > 0
                        ? 'grey'
                        : ''
                    }></img>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default Payment
